import React, { Suspense } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useThemeStore } from "../store/theme";
import { useMobileNavigation } from "../hooks/useMobileNavigation";
import TopNavigation from "./Navigation/TopNavigation";
import LeftSidebar from "./Navigation/LeftSidebar";
import RightSidebar from "./Navigation/RightSidebar";
import ErrorBoundary from "./ErrorBoundary";
import InteractiveTour from "./Tutorial/InteractiveTour";
import ComingSoon from "@/pages/ComingSoon";

const Discover = React.lazy(() => import("../pages/Discover/index"));
const Profile = React.lazy(() => import("../pages/Profile/Profile"));
const Settings = React.lazy(() => import("../pages/Settings/Settings"));
const Hub = React.lazy(() => import("../pages/Hub/Hub"));
const LLM = React.lazy(() => import("../pages/Spark/LLM"));
const Camera = React.lazy(() => import("../pages/Create/Camera"));
const View = React.lazy(() => import("../pages/View/View"));
const Post = React.lazy(() => import("../pages/Create/Post"));
const Edit = React.lazy(() => import("../pages/Create/Edit"));
const IP = React.lazy(() => import("../pages/Settings/IP"));
const Tutorial = React.lazy(() => import("../pages/Tutorial"));
const Search = React.lazy(() => import("../pages/Search"));
const Following = React.lazy(() => import("../pages/Following/Following"));
const Help = React.lazy(() => import("../pages/Settings/Help"));
const NotFound = React.lazy(() => import("../pages/NotFound"));
const Funding = React.lazy(() => import("../pages/Funding/index"));
const Investors = React.lazy(() => import("../pages/Investors/Investors"));
const Community = React.lazy(() => import("../pages/Community/Community"));
const PrivateChat = React.lazy(() => import("../pages/Chat/PrivateChat"));
const Checklists = React.lazy(() => import("../pages/Checklists/index"));

const Layout: React.FC = () => {
  const { colors, theme } = useThemeStore();
  const location = useLocation();
  const { isMobile } = useMobileNavigation();

  // Apply theme colors to CSS variables
  React.useEffect(() => {
    document.documentElement.style.setProperty(
      "--hover-bg",
      theme === "light" ? "#f3f4f6" : "#374151",
    );
    document.documentElement.style.setProperty("--bg-color", colors.background);
    document.documentElement.style.setProperty("--text-color", colors.text);
    document.documentElement.style.setProperty(
      "--border-color",
      theme === "light" ? "#e5e7eb" : "#374151",
    );

    // Apply theme class to body for global styling
    document.body.className = theme;
  }, [colors, theme]);

  // Check if current route matches specific layout conditions
  const isProfileRoute = location.pathname.startsWith("/profile");
  const isCommunityRoute = location.pathname.startsWith("/community");
  const isSearchRoute = location.pathname.startsWith("/search");
  const isSparkRoute = location.pathname.startsWith("/llm");
  const isHelpRoute = location.pathname.startsWith("/help");
  const isChecklistsRoute = location.pathname.startsWith("/checklists");
  const isInvestorsRoute = location.pathname.startsWith("/investors");
  const isPreviewRoute = location.pathname.startsWith("/p/");

  const isMaxWidth = (
    isProfileRoute ||
    isCommunityRoute ||
    isSearchRoute ||
    isSparkRoute ||
    isHelpRoute ||
    isChecklistsRoute ||
    isInvestorsRoute ||
    isPreviewRoute
  );

  return (
    <div
      className="idearify-layout"
      style={{ backgroundColor: colors.background }}
    >
      <InteractiveTour />
      <TopNavigation />

      <div className="flex pt-16">
        {/* Left Sidebar - Navigation (Desktop Only) */}
        {!isMobile && <LeftSidebar />}

        {/* Main Content Area */}
        <main
          className={`flex-1 ${!isMobile
              ? `ml-64 ${isProfileRoute || isPreviewRoute ? "mr-0" : "mr-80"}`
              : "mx-0"
            } ${isMobile ? "pb-20" : ""}`}
        >
          <div
            className={`idearify-main-content${isMaxWidth ? " max-w-full" : ""}`}
          >
            <ErrorBoundary>
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={location.pathname}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="flex-1"
                  style={{ height: "inherit" }}
                >
                  <Suspense
                    fallback={
                      <div className="flex h-full items-center justify-center p-8">
                        <div className="animate-pulse flex flex-col items-center">
                          <div className="w-12 h-12 rounded-full border-4 border-t-blue-500 border-gray-200 animate-spin mb-4"></div>
                          <div className="text-gray-500">Loading...</div>
                        </div>
                      </div>
                    }
                  >
                    <Routes location={location} key={location.pathname}>
                      <Route path="/" element={<ComingSoon />} />
                      {/* orginally discover was here */}
                      {/* <Route path="/discover" element={<Discover />} />
                      <Route path="/following" element={<Following />} />
                      <Route path="/profile/:username?" element={<Profile />} />
                      <Route path="/settings" element={<Settings />} />
                      <Route path="/hub" element={<Hub />} />
                      <Route path="/llm" element={<LLM />} />
                      <Route path="/camera" element={<Camera />} />
                      <Route path="/view/:id" element={<View />} />
                      <Route path="/post" element={<Post />} />
                      <Route path="/edit/:id" element={<Edit />} />

                      <Route path="/ip" element={<IP />} />
                      <Route path="/tutorial" element={<Tutorial />} />
                      <Route path="/search" element={<Search />} />
                      <Route path="/help" element={<Help />} />
                      <Route path="/checklists" element={<Checklists />} />
                      <Route path="/funding" element={<Funding />} />
                      <Route path="/investors" element={<Investors />} />
                      <Route path="/community" element={<Community />} />
                      <Route path="/chat/:username" element={<PrivateChat />} /> */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </motion.div>
              </AnimatePresence>
            </ErrorBoundary>
          </div>
        </main>
        {/* Right Sidebar - Suggested Content (Desktop Only, hidden on profile & preview routes) */}
        {!isMobile && !isProfileRoute && !isPreviewRoute && <RightSidebar />}
      </div>
    </div>
  );
};

export default Layout;